import type { DeckCardThumbnailSummary } from "@/cards/common/types/app-deck/deckCardThumbnail.types";
import { reactionEmoji } from "@/shared/constants/cardLayout";
import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import FavoriteBorderRoundedIcon from "@mui/icons-material/FavoriteBorderRounded";
import RedeemOutlinedIcon from "@mui/icons-material/RedeemOutlined";
import { Box, Stack, Typography } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";

type CardEngagementPanelProps = {
  summary?: DeckCardThumbnailSummary | null;
  viewerUsername?: string;
};

const statIconSx = {
  fontSize: 18,
  color: "var(--card-dark-text-secondary)",
} as const;

const CardEngagementPanel: React.FC<CardEngagementPanelProps> = ({ summary, viewerUsername }) => {
  const { t } = useTranslation("taascard");

  const stats = [
    {
      key: "reactions",
      icon: <FavoriteBorderRoundedIcon sx={statIconSx} />,
      label: t("deck_summary_reactions"),
      value: summary?.total_reactions ?? 0,
    },
    {
      key: "comments",
      icon: <ChatBubbleOutlineRoundedIcon sx={statIconSx} />,
      label: t("deck_summary_comments"),
      value: summary?.total_comments ?? 0,
    },
    {
      key: "gifts",
      icon: <RedeemOutlinedIcon sx={statIconSx} />,
      label: t("deck_summary_gifts"),
      value: summary?.total_gifts ?? 0,
    },
  ];

  const renderActivity = (
    items: DeckCardThumbnailSummary["recent_reactions"] | undefined,
    emptyLabel: string
  ) =>
    !items?.length ? (
      <Typography
        sx={{
          font: "var(--body-xs)",
          color: "var(--card-dark-text-secondary)",
        }}
      >
        {emptyLabel}
      </Typography>
    ) : (
      <Stack spacing="var(--spacing-2x)">
        {items.slice(0, 3).map((item, index) => (
          <Box
            key={`${item.username}-${index}`}
            sx={{
              p: "var(--spacing-2x)",
              borderRadius: "var(--spacing-2x)",
              bgcolor: "#241C18",
              border: "var(--stroke-xs) solid var(--card-dark-border)",
            }}
          >
            <Typography
              sx={{
                font: "var(--body-xs)",
                color: "#807356",
                fontWeight: 600,
              }}
            >
              {item.username ?? viewerUsername ?? t("fan")}
            </Typography>
            <Typography
              sx={{
                mt: "var(--spacing-1x)",
                font: "var(--body-xs)",
                color: "var(--card-dark-text)",
              }}
            >
              {item.type === "gift"
                ? "🎁 "
                : item.type && item.type !== "comment" && item.type !== "reply"
                  ? `${reactionEmoji(item.type)} `
                  : ""}
              {item.text || item.type || "—"}
            </Typography>
          </Box>
        ))}
      </Stack>
    );

  return (
    <Box
      sx={{
        borderRadius: "var(--spacing-3x)",
        bgcolor: "#241D13",
        border: "var(--stroke-xs) solid #362C19",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          px: "var(--spacing-2x)",
          py: "var(--spacing-3x)",
        }}
      >
        {stats.map((stat, index) => (
          <Box
            key={stat.key}
            sx={{
              textAlign: "center",
              px: "var(--spacing-1x)",
              borderLeft:
                index > 0 ? "var(--stroke-xs) solid var(--card-dark-text-secondary)" : undefined,
            }}
          >
            {stat.icon}
            <Typography
              sx={{
                mt: "var(--spacing-1x)",
                font: "var(--semibold-lg)",
                color: "var(--card-dark-text-secondary)",
              }}
            >
              {stat.value}
            </Typography>
            <Typography
              sx={{
                mt: "var(--spacing-1x)",
                font: "var(--semibold-xxs)",
                color: "#807356",
                textTransform: "uppercase",
              }}
            >
              {stat.label}
            </Typography>
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          mx: "var(--spacing-3x)",
          mb: "var(--spacing-3x)",
          borderTop: "var(--stroke-xs) solid var(--card-dark-text-secondary)",
          pt: "var(--spacing-3x)",
        }}
      >
        <Stack spacing="var(--spacing-3x)">
          <Box>
            <Typography
              sx={{
                mb: "var(--spacing-2x)",
                font: "var(--body-xs)",
                color: "#807356",
              }}
            >
              {t("deck_summary_recent_reactions")}
            </Typography>
            {renderActivity(summary?.recent_reactions, t("deck_summary_no_reactions"))}
          </Box>

          <Box>
            <Typography
              sx={{
                mb: "var(--spacing-2x)",
                font: "var(--body-xs)",
                color: "#807356",
              }}
            >
              {t("deck_summary_recent_comments")}
            </Typography>
            {renderActivity(summary?.recent_comments, t("deck_summary_no_comments"))}
          </Box>

          <Box>
            <Typography
              sx={{
                mb: "var(--spacing-2x)",
                font: "var(--body-xs)",
                color: "#807356",
              }}
            >
              {t("deck_summary_recent_replies")}
            </Typography>
            {renderActivity(summary?.recent_talent_replies, t("deck_summary_no_replies"))}
          </Box>
        </Stack>
      </Box>
    </Box>
  );
};

export default CardEngagementPanel;
